'use client';

import { tw } from '@tw';
import { catalogueFor } from '@peach/types/tutors/claim-page.consts.js';
import { weekLines } from '@/lib/availability/blocks';
import { claimedUsername } from '@/lib/page-address';
import { useInvitePage } from '@/lib/stores/invite-page.store';

const usd = (n: number) => `$${n.toLocaleString('en-US')}`;

/** A line still to be written: the shape of the text, never a placeholder sentence. */
const Ghost = ({ className }: { className: string }) => (
  <span aria-hidden className={tw('block h-2.5 rounded-full bg-[var(--sf-line)]', className)} />
);

/**
 * The page as it will go live, built from the same `catalogueFor` the claim uses, so the
 * prices here are the ones clients will pay. Each answer lands in it as it is typed.
 */
export function PagePreview({ compact = false }: { compact?: boolean }) {
  const { name, tagline, address, week, prices, step } = useInvitePage();
  const priced = step >= 3;
  const { trialBundle, bundles } = catalogueFor(prices);
  const products = [...(trialBundle ? [trialBundle] : []), ...bundles];
  const hours = weekLines(week);
  const link = address?.status === 'free' ? claimedUsername(address) : null;
  const shown = name.trim();
  const initial = shown.charAt(0).toUpperCase();

  // Below `deck` the full card would sit under the fold; the question keeps this one line above it.
  if (compact) {
    return (
      <section
        aria-label="Your page"
        className="flex items-center gap-3 rounded-[var(--sf-radius)] bg-[var(--sf-card)] p-3 text-[var(--sf-ink)] deck:hidden"
      >
        <Avatar initial={initial} small />
        <div className="flex min-w-0 flex-1 flex-col">
          {shown && <span className="truncate text-p-3 font-semibold">{shown}</span>}
          <span className="truncate text-badge text-[var(--sf-mut)]">meetmy.io/{link ?? '…'}</span>
        </div>
        {trialBundle && priced && (
          <span className="sf-num shrink-0 rounded-md bg-[var(--sf-lime)] px-1.5 text-p-3 font-bold text-[var(--sf-on-lime)]">
            {usd(trialBundle.price)}
          </span>
        )}
      </section>
    );
  }

  return (
    <section
      aria-label="Your page"
      className="flex flex-col gap-3 rounded-[var(--sf-radius)] bg-[var(--sf-card)] p-3.5 text-[var(--sf-ink)]"
    >
      <div className="flex items-center gap-3">
        <Avatar initial={initial} />
        <div className="flex min-w-0 flex-1 flex-col gap-1.5">
          {/* Keyed on arriving, not on every keystroke: the line lands once, then just updates. */}
          {shown ? (
            <p key="name" className="mm-land sf-title text-head-2 font-extrabold leading-tight">
              {shown}
            </p>
          ) : (
            <Ghost className="w-2/3" />
          )}
          {tagline.trim() ? (
            <p key="tagline" className="mm-land text-p-3 leading-snug text-[var(--sf-mut)]">
              {tagline.trim()}
            </p>
          ) : (
            <Ghost className="w-1/2" />
          )}
        </div>
      </div>
      {link && (
        <p className="w-fit max-w-full truncate rounded-full bg-[var(--sf-ticket)] px-3 py-1 text-p-3 font-semibold">
          meetmy.io/{link}
        </p>
      )}
      {/* No price before the price step, where they are set; there the ring points at them. */}
      {priced ? (
        <ul
          className={tw(
            'flex flex-col rounded-md px-2 ring-1 transition-shadow',
            step === 3 ? 'ring-[var(--sf-ink)]' : 'ring-transparent'
          )}
        >
          {products.map((p) => (
            <li
              key={`${p.type}-${p.numberOfClasses}`}
              className="flex items-baseline gap-2 border-b border-[var(--sf-line)] py-2.5 text-p-3 last:border-b-0"
            >
              <span className="min-w-0 flex-1 truncate font-semibold">{p.title}</span>
              <span className="text-badge text-[var(--sf-mut)]">
                {p.numberOfClasses > 1 ? `${p.numberOfClasses} calls` : `${p.duration} min`}
              </span>
              {p.previousPrice ? (
                <s className="sf-num text-badge text-[var(--sf-mut)]">{usd(p.previousPrice)}</s>
              ) : null}
              <span className="sf-num rounded-md bg-[var(--sf-lime)] px-1.5 font-bold text-[var(--sf-on-lime)]">
                {usd(p.price)}
              </span>
            </li>
          ))}
        </ul>
      ) : (
        <ul aria-hidden className="flex flex-col px-2">
          {products.map((p) => (
            <li
              key={`${p.type}-${p.numberOfClasses}`}
              className="flex items-center gap-2 border-b border-[var(--sf-line)] py-3 last:border-b-0"
            >
              <span className="min-w-0 flex-1 truncate text-p-3 font-semibold">{p.title}</span>
              <Ghost className="w-10" />
            </li>
          ))}
        </ul>
      )}
      <p
        key={hours.join()}
        className={tw(
          'mm-land rounded-md px-2 py-1 text-p-3 text-[var(--sf-mut)] ring-1 transition-shadow',
          step === 2 ? 'ring-[var(--sf-ink)]' : 'ring-transparent'
        )}
      >
        {hours.length ? `Open ${hours.join(', ')}` : 'No hours open yet'}
      </p>
    </section>
  );
}

function Avatar({ initial, small = false }: { initial: string; small?: boolean }) {
  return (
    <span
      aria-hidden
      className={tw(
        'sf-title grid shrink-0 place-items-center rounded-full bg-[var(--sf-ticket)] font-extrabold',
        small ? 'size-9 text-p-3' : 'size-12 text-head-2'
      )}
    >
      {initial}
    </span>
  );
}
